// A successful API call → the tutorial event it confirms. Reads the method and path only; the one
// exception is the review action enum (reopen), taken from the request, never any other body text.
export interface MappedEvent {
  event: string
  workspaceId: string | null
  /** The studio item, from …/items/{id} paths only. */
  entityId: string | null
  /** Counts only after this event has succeeded earlier in the session. */
  after?: string
}

type Rule = [route: string, events: string[], opts?: { after?: string; action?: string }]

const RULES: Rule[] = [
  ['POST /auth/login', ['authenticated']],
  ['POST /auth/verify-email', ['verification-confirmed']],
  ['POST /auth/accept-terms', ['consent-confirmed']],
  ['POST /auth/ai-disclosure-accept', ['consent-confirmed']],
  ['POST /workspaces', ['workspace-created']],
  ['POST /workspaces/:w/activate', ['workspace-activated']],
  ['POST /workspaces/:w/sources', ['source-created']],
  ['POST /workspaces/:w/sources/upload', ['source-created']],
  ['POST /workspaces/:w/sources/:id/analyze', ['source-processed']],
  ['GET /workspaces/:w/research-proposals', ['source-processed', 'selection-reviewed']],
  ['GET /workspaces/:w/research-proposals/:id', ['selection-reviewed']],
  ['POST /workspaces/:w/research-proposals/:id/apply', ['changes-applied']],
  ['POST /workspaces/:w/products', ['product-saved']],
  ['PATCH /workspaces/:w/products/:id', ['product-saved']],
  ['POST /workspaces/:w/services', ['product-saved']],
  ['PUT /workspaces/:w/services/:id', ['product-saved']],
  ['POST /workspaces/:w/personas', ['persona-saved']],
  ['PUT /workspaces/:w/personas/:id', ['persona-saved']],
  ['POST /workspaces/:w/personas/:id/primary', ['persona-saved']],
  ['PUT /workspaces/:w/voice', ['voice-saved']],
  ['PATCH /workspaces/:w/visual-brand', ['visual-brand-saved']],
  ['GET /workspaces/:w/visual-brand', ['brand-reloaded']],
  ['PATCH /workspaces/:w/strategy', ['strategy-saved', 'cadence-saved']],
  ['GET /workspaces/:w/draft-sets/:s', ['draft-reloaded']],
  ['POST /workspaces/:w/draft-sets/:s/items/:i/revisions', ['revision-saved']],
  ['POST /workspaces/:w/draft-sets/:s/items/:i/refinements', ['refinement-created']],
  ['POST /workspaces/:w/draft-sets/:s/refinements/:p/accept', ['proposal-resolved']],
  ['POST /workspaces/:w/draft-sets/:s/refinements/:p/discard', ['proposal-resolved']],
  ['POST /workspaces/:w/draft-sets/:s/revisions/:r/restore', ['revision-restored']],
  ['POST /workspaces/:w/draft-sets/:s/items/:i/review', ['review-reopened'], { action: 'reopen' }],
  ['PATCH /posts/:id/image', ['media-saved']],
  ['POST /collections', ['collection-saved']],
  ['PATCH /collections/:id', ['collection-saved']],
  ['PATCH /posts/:id/collection', ['collection-saved']],
  ['GET /history/workspaces/:w/sessions/:s', ['history-restored']],
  ['POST /prompts', ['prompt-saved']],
  ['PUT /prompts/:id', ['prompt-saved']],
  ['POST /drip/answer', ['drip-saved']],
  ['POST /calendar-notes/:date', ['idea-saved']],
  ['POST /workspaces/:w/ideas', ['idea-saved']],
  ['POST /workspaces/:w/delivery-preview', ['schedule-previewed']],
  ['POST /workspaces/:w/facts', ['fact-saved']],
  ['PATCH /workspaces/:w/facts/:id', ['fact-saved']],
  ['POST /workspaces/:w/proof/:f/permission', ['permission-saved']],
  ['POST /workspaces/:w/harvest/:i/confirm', ['harvest-resolved']],
  ['POST /workspaces/:w/harvest/:i/reject', ['harvest-resolved']],
  ['POST /workspaces/:w/moments', ['moment-saved']],
  ['PATCH /workspaces/:w/learning/preferences', ['rule-saved']],
  ['POST /workspaces/:w/analytics/imports/file', ['batch-created']],
  ['POST /workspaces/:w/analytics/imports/screenshot', ['batch-created']],
  ['POST /workspaces/:w/analytics/imports/file/confirm-mapping', ['mapping-saved']],
  ['POST /workspaces/:w/analytics/imports/:b/confirm', ['batch-confirmed']],
  ['POST /case-studies/generate', ['case-study-generated']],
  ['GET /case-studies/:cs', ['case-study-generated-and-retrieved'], { after: 'case-study-generated' }],
  ['PATCH /me/app-preferences', ['preferences-saved']],
  ['PATCH /workspaces/:w/preferences', ['notification-preferences-saved']],
]

const RESERVED = '(?!keywords$|calendar$|generate$|upload$|file$|screenshot$)'
const COMPILED = RULES.map(([route, events, opts]) => {
  const [method, path] = route.split(' ')
  const pattern = path.replace(/:(\w+)/g, (_, name: string) => `(?<${name}>${RESERVED}[^/]+)`)
  return { method, re: new RegExp(`^${pattern}$`), events, opts: opts ?? {} }
})

/** `/api/v1/x?y` or `http://h/api/v1/x` → `/x`. */
function pathOf(url: string): string {
  const noQuery = url.split(/[?#]/)[0].replace(/^https?:\/\/[^/]+/, '')
  return noQuery.replace(/^\/api\/v1(?=\/)/, '').replace(/\/+$/, '') || '/'
}

function actionOf(data: unknown): string | null {
  let parsed = data
  if (typeof data === 'string') {
    try { parsed = JSON.parse(data) } catch { return null }
  }
  const action = parsed && typeof parsed === 'object' ? (parsed as { action?: unknown }).action : null
  return typeof action === 'string' ? action : null
}

export function mapRequest(method: string, url: string, data?: unknown): MappedEvent[] {
  const path = pathOf(url)
  const verb = method.toUpperCase()
  for (const rule of COMPILED) {
    if (rule.method !== verb) continue
    const match = rule.re.exec(path)
    if (!match) continue
    if (rule.opts.action && actionOf(data) !== rule.opts.action) return []
    const groups = match.groups ?? {}
    const workspaceId = groups.w ?? null
    const entityId = groups.i ?? null
    return rule.events.map((event) => ({ event, workspaceId, entityId, ...(rule.opts.after ? { after: rule.opts.after } : {}) }))
  }
  return []
}
